import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { MdArrowBack, MdSave, MdStore } from 'react-icons/md';
import { toast } from 'react-toastify';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import StatsGrid from '../../../shared/layout/containers/StatsGrid';
import StatCard from '../../../shared/layout/cards/StatCard';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../shared/layout/feedback/ErrorMessage';
import TabBar from '../../../shared/layout/tabs/TabBar';
import TabButton from '../../../shared/layout/tabs/TabButton';
import MainButton from '../../../shared/layout/buttons/MainButton';
import AdminDataTable from './components/AdminDataTable';
import AdminOrganizationCategoryMappings from './AdminOrganizationCategoryMappings';
import { SimpleSelect } from '../../../shared/layout/forms/AppSelect';
import { updateOrganizationPublicPartner } from '../../../services/Http/AdminApi';
import CountrySelect from '../../../shared/layout/forms/CountrySelect';
import {
  fetchAdminOrganization,
  fetchAdminOrganizationOverview,
  fetchAdminOrganizationVenues,
  fetchAdminOrganizationUsers,
  fetchAdminOrganizationIntegrationsSummary,
  updateAdminOrganization,
  updateAdminOrganizationSettings,
} from '../../../services/Http/AdminApi';
import { fetchCountryCodes, fetchOrganizationTypes, fetchCurrencies, fetchTimezones, fetchLanguages } from '../../../services/Http/Api';

const TABS = [
  { id: 'profile', label: 'Profile' },
  { id: 'users', label: 'Users' },
  { id: 'venues', label: 'Venues' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'category-mappings', label: 'Category mappings' },
  { id: 'settings', label: 'Settings' },
  { id: 'partner', label: 'Public partner' },
];


// "Prije 3 dana" odgovara na pitanje zbog kojeg se ovdje i gleda - koristi li
// netko sustav - brze nego datum koji treba usporediti s danasnjim.
const formatWhen = (value) => {
  if (!value) return '—';

  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return '—';

  const days = Math.floor((Date.now() - then.getTime()) / 86400000);

  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  if (days < 365) return `${Math.floor(days / 30)} mo ago`;

  return then.toLocaleDateString();
};

const AdminOrganizationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);
  const [error, setError] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [partnerForm, setPartnerForm] = useState({
    is_public_partner: false, slug: '', partner_since: '', public_description: '', partner_sort_order: 0,
  });
  const [savingPartner, setSavingPartner] = useState(false);
  const [overview, setOverview] = useState(null);
  const [orgUsers, setOrgUsers] = useState([]);
  const [orgVenues, setOrgVenues] = useState([]);
  const [integrationSummary, setIntegrationSummary] = useState(null);
  const [types, setTypes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [orgForm, setOrgForm] = useState(defaultOrgForm());
  const [settingsForm, setSettingsForm] = useState(defaultSettingsForm());
  const [savingGeneral, setSavingGeneral] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const loadLookups = useCallback(async () => {
    try {
      const [orgTypes, countryCodes, currs, tzs, langs] = await Promise.all([
        fetchOrganizationTypes(), fetchCountryCodes(), fetchCurrencies(), fetchTimezones(), fetchLanguages(),
      ]);
      setTypes(Array.isArray(orgTypes) ? orgTypes : []);
      setCountries(Array.isArray(countryCodes) ? countryCodes : []);
      setCurrencies(Array.isArray(currs) ? currs : []);
      setTimezones(Array.isArray(tzs) ? tzs : []);
      setLanguages(Array.isArray(langs) ? langs : []);
    } catch {
      // best effort
    }
  }, []);

  const syncFormsFromOrganization = useCallback((data) => {
    setOrgForm({
      name: data?.name || '', email: data?.email || '', phone: data?.phone || '', address: data?.address || '',
      country_id: data?.country_id || '', organization_type_id: data?.organization_type_id || '', active: data?.active ?? true,
    });
    const settings = data?.settings || {};
    setSettingsForm({
      currency_id: settings.currency_id || '', timezone_id: settings.timezone_id || '', language_id: settings.language_id || '',
      billing_email: settings.billing_email || '', support_email: settings.support_email || '', support_phone: settings.support_phone || '',
      send_notifications_via_email: settings.send_notifications_via_email ?? false,
      send_notifications_via_sms: settings.send_notifications_via_sms ?? false,
      send_notifications_via_push: settings.send_notifications_via_push ?? false,
      auto_payment_enabled: settings.auto_payment_enabled ?? false,
    });
  }, []);

  const loadBase = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [orgResp, overviewResp] = await Promise.all([
        fetchAdminOrganization(id),
        fetchAdminOrganizationOverview(id),
      ]);
      setOrganization(orgResp);
      setPartnerForm({
        is_public_partner: Boolean(orgResp?.is_public_partner),
        slug: orgResp?.slug || '',
        partner_since: (orgResp?.partner_since || '').slice(0, 10),
        public_description: orgResp?.public_description || '',
        partner_sort_order: orgResp?.partner_sort_order ?? 0,
      });
      setOverview(overviewResp?.data || null);
      syncFormsFromOrganization(orgResp);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [id, syncFormsFromOrganization]);

  const loadTabData = useCallback(async (tabId) => {
    if (!id) return;
    try {
      setTabLoading(true);
      if (tabId === 'users') {
        const resp = await fetchAdminOrganizationUsers(id, { per_page: 50 });
        setOrgUsers(resp?.data || []);
      } else if (tabId === 'venues') {
        const resp = await fetchAdminOrganizationVenues(id, { per_page: 50 });
        setOrgVenues(resp?.data || []);
      } else if (tabId === 'integrations') {
        const resp = await fetchAdminOrganizationIntegrationsSummary(id);
        setIntegrationSummary(resp?.data || null);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || `Failed to load ${tabId}`);
    } finally {
      setTabLoading(false);
    }
  }, [id]);

  useEffect(() => { loadLookups(); }, [loadLookups]);
  useEffect(() => { if (id) loadBase(); }, [id, loadBase]);
  useEffect(() => { if (['users', 'venues', 'integrations'].includes(activeTab)) loadTabData(activeTab); }, [activeTab, loadTabData]);

  const handleSaveGeneral = async (e) => {
    e.preventDefault();
    try {
      setSavingGeneral(true);
      await updateAdminOrganization(id, orgForm);
      await loadBase();
      toast.success('Organization updated');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save organization');
    } finally {
      setSavingGeneral(false);
    }
  };

  const handleSavePartner = async (e) => {
    e.preventDefault();
    try {
      setSavingPartner(true);
      const saved = await updateOrganizationPublicPartner(id, {
        ...partnerForm,
        slug: partnerForm.slug || null,
        partner_since: partnerForm.partner_since || null,
        partner_sort_order: Number(partnerForm.partner_sort_order) || 0,
      });
      setPartnerForm((f) => ({ ...f, slug: saved.slug || '', partner_since: saved.partner_since || '' }));
      await loadBase();
      toast.success(saved.is_public_partner ? 'Partner is now public' : 'Partner hidden from the landing page');
    } catch (err) {
      const errors = err?.response?.data?.errors;
      toast.error(errors ? Object.values(errors).flat().join(' ') : 'Failed to save');
    } finally {
      setSavingPartner(false);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    try {
      setSavingSettings(true);
      await updateAdminOrganizationSettings(id, settingsForm);
      await loadBase();
      toast.success('Organization settings updated');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const venueColumns = useMemo(() => ([
    { key: 'name', label: 'Name' },
    { key: 'city', label: 'City' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'is_api_active', label: 'API', render: (r) => (r.is_api_active ? 'Active' : 'Inactive') },
    { key: 'virtual_source', label: 'Source', render: (r) => r.virtual_source || 'real' },
    { key: 'integrations_count', label: 'Integrations' },
  ]), []);

  const usersColumns = useMemo(() => ([
    { key: 'name', label: 'Name', render: (r) => `${r.name || ''} ${r.surname || ''}`.trim() || '—' },
    { key: 'email', label: 'Email' },
    { key: 'phone_number', label: 'Phone' },
    { key: 'roles', label: 'Roles', render: (r) => (r.roles || []).map((x) => x.name).join(', ') || '—' },
    { key: 'has_default_password', label: 'Default Password', render: (r) => r.has_default_password ? <Chip $tone="warning">Yes</Chip> : <Chip>No</Chip> },
    { key: 'last_login_at', label: 'Last login', render: (r) => formatWhen(r.last_login_at) },
    { key: 'last_activity_at', label: 'Last active', render: (r) => formatWhen(r.last_activity_at) },
    { key: 'created_at', label: 'Created', render: (r) => r.created_at ? new Date(r.created_at).toLocaleDateString() : '—' },
  ]), []);

  if (loading) return <LoadingMessage>Loading organization...</LoadingMessage>;
  if (error || !organization) return <ErrorMessage>Unable to load organization.</ErrorMessage>;

  return (
    <PageContainer>
      <PageHeader>
        <BackButton type="button" onClick={() => navigate('/organizations')}>
          <MdArrowBack size={18} /> Back to list
        </BackButton>
        <PageTitle>{organization.name}</PageTitle>
      </PageHeader>
      <PageContent>
        <StatsGrid>
          <StatCard icon={<MdStore size={40} />} title="Venues" value={overview?.counts?.venues ?? (organization.venues || []).length} color="venues" />
          <StatCard icon="U" title="Users" value={overview?.counts?.users ?? 0} color="transactions" />
          <StatCard icon="I" title="Integrations" value={overview?.counts?.integrations ?? 0} color="info" />
          <StatCard icon="O" title="Onboarding" value={`${overview?.onboarding?.completion_percent ?? 0}%`} color="success" />
          <StatCard
            icon="A"
            title="Active (30d)"
            value={overview?.counts?.users_active_last_30_days ?? 0}
            color="revenue"
          />
          <StatCard
            icon="L"
            title="Last activity"
            value={formatWhen(overview?.activity?.last_activity_at)}
            color="users"
          />
        </StatsGrid>

        <TabBar>
          {TABS.map((tab) => (
            <TabButton key={tab.id} active={activeTab === tab.id} onClick={() => setActiveTab(tab.id)}>
              {tab.label}
            </TabButton>
          ))}
        </TabBar>

        {activeTab === 'profile' && (
          <Card>
            <SectionHeader>General</SectionHeader>
            <Form onSubmit={handleSaveGeneral}>
              <Row>
                <Input value={orgForm.name} onChange={(e) => setOrgForm((f) => ({ ...f, name: e.target.value }))} placeholder="Name" required />
                <Select value={orgForm.organization_type_id} onChange={(e) => setOrgForm((f) => ({ ...f, organization_type_id: e.target.value }))} required>
                  <option value="">Organization type</option>
                  {types.map((t) => <option key={t.id || t.value} value={t.id || t.value}>{t.name || t.label}</option>)}
                </Select>
              </Row>
              <Row>
                <Input type="email" value={orgForm.email} onChange={(e) => setOrgForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email" />
                <Input value={orgForm.phone} onChange={(e) => setOrgForm((f) => ({ ...f, phone: e.target.value }))} placeholder="Phone" />
              </Row>
              <Row>
                <CountrySelect
                  countries={countries}
                  value={orgForm.country_id}
                  onChange={(_, nextValue) => setOrgForm((f) => ({ ...f, country_id: nextValue || '' }))}
                  inputId="organization-detail-country"
                  placeholder="Country"
                  menuPortalTarget={document.body}
                />
                <Input value={orgForm.address} onChange={(e) => setOrgForm((f) => ({ ...f, address: e.target.value }))} placeholder="Address" />
              </Row>
              <Row>
                <CheckLabel><input type="checkbox" checked={!!orgForm.active} onChange={(e) => setOrgForm((f) => ({ ...f, active: e.target.checked }))} /><span>Active</span></CheckLabel>
              </Row>
              <Actions><MainButton type="submit" disabled={savingGeneral}>{savingGeneral ? 'Saving...' : 'Save General'}</MainButton></Actions>
            </Form>
          </Card>
        )}

        {activeTab === 'partner' && (
          <Card>
            <SectionHeader>Public partner</SectionHeader>
            <Form onSubmit={handleSavePartner}>
              <Row>
                <CheckLabel>
                  <input
                    type="checkbox"
                    checked={!!partnerForm.is_public_partner}
                    onChange={(e) => setPartnerForm((f) => ({ ...f, is_public_partner: e.target.checked }))}
                  />
                  <span>Show on the landing page</span>
                </CheckLabel>
              </Row>
              <Row>
                <Input
                  value={partnerForm.slug}
                  onChange={(e) => setPartnerForm((f) => ({ ...f, slug: e.target.value }))}
                  placeholder="URL slug (e.g. egarner)"
                />
                <Input
                  type="date"
                  value={partnerForm.partner_since}
                  onChange={(e) => setPartnerForm((f) => ({ ...f, partner_since: e.target.value }))}
                  placeholder="Partner since"
                />
                <Input
                  type="number"
                  min="0"
                  value={partnerForm.partner_sort_order}
                  onChange={(e) => setPartnerForm((f) => ({ ...f, partner_sort_order: e.target.value }))}
                  placeholder="Sort order"
                />
              </Row>
              <Row>
                <Input
                  value={partnerForm.public_description}
                  onChange={(e) => setPartnerForm((f) => ({ ...f, public_description: e.target.value }))}
                  placeholder="Public description (shown on the partner page)"
                />
              </Row>
              <Hint>
                Leave the slug empty and we suggest one from the name. Nothing here is derived
                automatically &mdash; demo organizations carry bigger catalogues than real partners,
                so publishing is always a deliberate choice.
              </Hint>
              {partnerForm.is_public_partner && partnerForm.slug && (
                <Hint>Public page: /partners/{partnerForm.slug}</Hint>
              )}
              <Actions>
                <MainButton type="submit" disabled={savingPartner}>
                  {savingPartner ? 'Saving…' : 'Save'}
                </MainButton>
              </Actions>
            </Form>
          </Card>
        )}

        {activeTab === 'settings' && (
          <Card>
            <SectionHeader>Settings</SectionHeader>
            <Form onSubmit={handleSaveSettings}>
              <Row>
                <Select value={settingsForm.currency_id} onChange={(e) => setSettingsForm((f) => ({ ...f, currency_id: e.target.value }))}><option value="">Currency</option>{currencies.map((c) => <option key={c.id || c.code} value={c.id || c.code}>{c.name || c.code}</option>)}</Select>
                <Select value={settingsForm.timezone_id} onChange={(e) => setSettingsForm((f) => ({ ...f, timezone_id: e.target.value }))}><option value="">Timezone</option>{timezones.map((t) => <option key={t.id || t.name} value={t.id || t.name}>{t.name}</option>)}</Select>
                <Select value={settingsForm.language_id} onChange={(e) => setSettingsForm((f) => ({ ...f, language_id: e.target.value }))}><option value="">Language</option>{languages.map((l) => <option key={l.id || l.code} value={l.id || l.code}>{l.name || l.code}</option>)}</Select>
              </Row>
              <Row>
                <Input value={settingsForm.billing_email} onChange={(e) => setSettingsForm((f) => ({ ...f, billing_email: e.target.value }))} placeholder="Billing email" />
                <Input value={settingsForm.support_email} onChange={(e) => setSettingsForm((f) => ({ ...f, support_email: e.target.value }))} placeholder="Support email" />
                <Input value={settingsForm.support_phone} onChange={(e) => setSettingsForm((f) => ({ ...f, support_phone: e.target.value }))} placeholder="Support phone" />
              </Row>
              <Row>
                <CheckLabel><input type="checkbox" checked={!!settingsForm.send_notifications_via_email} onChange={(e) => setSettingsForm((f) => ({ ...f, send_notifications_via_email: e.target.checked }))} /><span>Notify email</span></CheckLabel>
                <CheckLabel><input type="checkbox" checked={!!settingsForm.send_notifications_via_sms} onChange={(e) => setSettingsForm((f) => ({ ...f, send_notifications_via_sms: e.target.checked }))} /><span>Notify SMS</span></CheckLabel>
                <CheckLabel><input type="checkbox" checked={!!settingsForm.send_notifications_via_push} onChange={(e) => setSettingsForm((f) => ({ ...f, send_notifications_via_push: e.target.checked }))} /><span>Notify push</span></CheckLabel>
                <CheckLabel><input type="checkbox" checked={!!settingsForm.auto_payment_enabled} onChange={(e) => setSettingsForm((f) => ({ ...f, auto_payment_enabled: e.target.checked }))} /><span>Auto payment</span></CheckLabel>
              </Row>
              <Actions><MainButton type="submit" disabled={savingSettings}>{savingSettings ? 'Saving...' : 'Save Settings'}</MainButton></Actions>
            </Form>
          </Card>
        )}

        {activeTab === 'users' && (
          <Card>
            <SectionHeader>Organization Users</SectionHeader>
            {tabLoading && <LoadingMessage>Loading users...</LoadingMessage>}
            <AdminDataTable rows={orgUsers} columns={usersColumns} loading={tabLoading} emptyText="No organization users" />
          </Card>
        )}

        {activeTab === 'venues' && (
          <Card>
            <SectionHeader>Organization Venues</SectionHeader>
            {tabLoading && <LoadingMessage>Loading venues...</LoadingMessage>}
            <AdminDataTable rows={orgVenues} columns={venueColumns} loading={tabLoading} emptyText="No venues" />
          </Card>
        )}

        {activeTab === 'integrations' && (
          <Card>
            <SectionHeader>Integrations Summary</SectionHeader>
            {tabLoading && <LoadingMessage>Loading integrations summary...</LoadingMessage>}
            {!tabLoading && (
              <>
                <StatsGrid>
                  <StatCard icon="I" title="Total Integrators" value={integrationSummary?.counts?.total_integrators ?? 0} color="info" />
                  <StatCard icon="A" title="Active" value={integrationSummary?.counts?.active_integrators ?? 0} color="success" />
                  <StatCard icon="W" title="Webhooks" value={integrationSummary?.counts?.webhooks_total ?? 0} color="transactions" />
                  <StatCard icon="F" title="Webhook Failing" value={integrationSummary?.counts?.webhooks_failing ?? 0} color="warning" />
                </StatsGrid>
                <SectionHeader style={{ marginTop: 12 }}>By Type</SectionHeader>
                <AdminDataTable
                  rows={integrationSummary?.by_type || []}
                  columns={[
                    { key: 'integration_type', label: 'Type' },
                    { key: 'count', label: 'Count' },
                  ]}
                  loading={false}
                  emptyText="No integrations"
                />
              </>
            )}
          </Card>
        )}

        {activeTab === 'category-mappings' && (
          <Card>
            <SectionHeader>Organization category mappings</SectionHeader>
            <AdminOrganizationCategoryMappings organizationId={id} />
          </Card>
        )}
      </PageContent>
    </PageContainer>
  );
};

export default AdminOrganizationDetail;

const defaultOrgForm = () => ({ name: '', email: '', phone: '', address: '', country_id: '', organization_type_id: '', active: true });
const defaultSettingsForm = () => ({ currency_id: '', timezone_id: '', language_id: '', billing_email: '', support_email: '', support_phone: '', send_notifications_via_email: false, send_notifications_via_sms: false, send_notifications_via_push: false, auto_payment_enabled: false });

const BackButton = styled.button`
  display:inline-flex;align-items:center;gap: 0.5rem;border:1px solid ${({theme})=>theme.colors.border};background:${({theme})=>theme.colors.surface};padding: 0.5rem 0.75rem;border-radius:10px;cursor:pointer;color:${({theme})=>theme.colors.textPrimary};
`;
const Card = styled.div`background:${({theme})=>theme.colors.surface};border:1px solid ${({theme})=>theme.colors.border};border-radius:12px;padding: 1rem;margin-top: 1rem;`;
const SectionHeader = styled.h4`margin: 0 0 0.75rem;font-size:1rem;font-weight:700;color:${({theme})=>theme.colors.textPrimary};`;
const Form = styled.form`display:flex;flex-direction:column;gap: 0.75rem;`;
const Row = styled.div`display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap: 0.75rem;`;
const Input = styled.input`padding: 0.75rem 0.875rem;border:1px solid ${({theme})=>theme.colors.border};border-radius:10px;width:100%;font-size:0.95rem;background:${({theme})=>theme.colors.surface};color:${({theme})=>theme.colors.textPrimary};&:focus{border-color:var(--color-primary);box-shadow:0 0 0 3px var(--color-primary-bg);outline:none;}`;
const Select = styled(SimpleSelect)`padding: 0.75rem 0.875rem;border:1px solid ${({theme})=>theme.colors.border};border-radius:10px;width:100%;font-size:0.95rem;background:${({theme})=>theme.colors.surface};color:${({theme})=>theme.colors.textPrimary};&:focus{border-color:var(--color-primary);box-shadow:0 0 0 3px var(--color-primary-bg);outline:none;}`;
const Actions = styled.div`display:flex;gap: 0.625rem;justify-content:flex-start;`;
const Hint = styled.p`margin:0.35rem 0 0;font-size:0.78rem;opacity:0.65;`;
const CheckLabel = styled.label`display:flex;align-items:center;gap: 0.5rem;color:${({theme})=>theme.colors.textPrimary};font-weight:600;`;
const Chip = styled.span`
  display:inline-flex;align-items:center;padding: 0.25rem 0.5rem;border-radius:999px;font-size: 0.75rem;font-weight:700;
  background:${({theme,$tone}) => $tone === 'warning' ? theme.colors.warningBg : theme.colors.surfaceMuted};
  color:${({theme,$tone}) => $tone === 'warning' ? theme.colors.warning : theme.colors.textMuted};
`;
